"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/VStack";
import {
  capturePromoFromSearch,
  patchStoredPromo,
  readStoredPromo,
} from "@/lib/billing/promo-cookie";
import { PROMO_QUERY_KEY } from "@/lib/billing/promo-link";
import type { PromoCouponType } from "@/lib/billing/promo-benefit";
import {
  promoScheduleCopy,
  type PromoScheduleStatus,
} from "@/lib/billing/promo-schedule-copy";

type PromoAuthInfo = {
  code: string;
  type: PromoCouponType;
  benefitLabel: string;
  attachedTitle: string;
  nextStepHint: string;
  afterVerifyHint: string;
  available?: boolean;
  status?: PromoScheduleStatus;
  startsAt?: string | null;
  expiresAt?: string | null;
};

type Props = {
  /** На экране «проверьте почту» — другой текст следующего шага */
  variant?: "form" | "after_verify";
};

export function PromoAttachedBanner({ variant = "form" }: Props) {
  const searchParams = useSearchParams();
  const [info, setInfo] = useState<PromoAuthInfo | null>(null);

  useEffect(() => {
    const search = searchParams.toString();
    if (search && searchParams.get(PROMO_QUERY_KEY)) {
      capturePromoFromSearch(`?${search}`, { phase: "pending_auth" });
    }
    const storedBefore = readStoredPromo();
    const code = storedBefore?.code?.trim();
    if (!code || storedBefore?.phase === "done") {
      setInfo(null);
      return;
    }
    // На экранах auth промо ждёт логин/регистрацию → автоактивация.
    patchStoredPromo({ phase: "pending_auth", offerDismissed: true });

    let cancelled = false;
    void fetch(`/api/public/promo/${encodeURIComponent(code)}`, {
      cache: "no-store",
    })
      .then(async (res) => {
        if (!res.ok) {
          if (!cancelled) setInfo(null);
          return;
        }
        const data = (await res.json()) as PromoAuthInfo;
        if (!cancelled) setInfo(data);
      })
      .catch(() => {
        if (!cancelled) setInfo(null);
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  if (!info) return null;

  const schedule = promoScheduleCopy({
    status: info.status ?? (info.available === false ? "limit" : "ok"),
    startsAt: info.startsAt,
    expiresAt: info.expiresAt,
    benefitLabel: info.benefitLabel,
    kind: info.type === "free_credits" || info.type === "journey" ? "credits" : "discount",
  });

  const hint = schedule.isUnavailable
    ? schedule.bannerHint
    : variant === "after_verify"
      ? info.afterVerifyHint
      : info.nextStepHint;

  const title = schedule.isUnavailable
    ? `${info.code}: ${schedule.bannerTitle}`
    : info.attachedTitle;

  return (
    <VStack
      gap={1}
      align="start"
      width="100%"
      className={[
        "rounded-xl border px-4 py-3",
        schedule.isUnavailable
          ? "border-[var(--color-border-warning,#d97706)] bg-[var(--color-bg-warning,#fffbeb)]"
          : "border-primary/25 bg-primary/5",
      ].join(" ")}
    >
      <Text size="sm" weight="semibold">
        {title}
      </Text>
      <Text size="sm" weight="semibold" className="text-primary">
        {info.benefitLabel}
      </Text>
      <Text size="sm" color="secondary">
        {hint}
      </Text>
    </VStack>
  );
}
